import type { Faq, KnowledgeCategory, KnowledgeGap } from '@hostesa/api-client';
import {
  useAnswerGap,
  useDismissGap,
  useKnowledgeGaps,
  useMarkGapDuplicate,
  useResolveGap,
} from '@hostesa/api-client';
import { useT } from '@hostesa/i18n';
import {
  Button,
  EmptyState,
  ErrorState,
  Input,
  Label,
  LoadingState,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@hostesa/ui';
import { useState } from 'react';
import { namespace } from './i18n';
import { asNumber, asText, gapStatusVariant } from './lib';

const CATEGORIES: readonly KnowledgeCategory[] = [
  'hotel_profile',
  'rooms',
  'offers',
  'policies',
  'services',
];

/**
 * GapList — open knowledge gaps with triage actions. Resolve links the gap to
 * an existing FAQ; Answer creates a new FAQ (lifted to the page so it can show
 * the new FAQ in the FAQ tab). Approve/answer never send `approvedBy` — the
 * server records the session approver.
 */
export function GapList({
  tenantId,
  hotelId,
  onFaqCreated,
}: {
  tenantId: string;
  hotelId: string;
  onFaqCreated: (faq: Faq) => void;
}) {
  const { t } = useT(namespace);
  const gapsQuery = useKnowledgeGaps(tenantId, hotelId);

  const resolve = useResolveGap(tenantId, hotelId);
  const dismiss = useDismissGap(tenantId, hotelId);
  const duplicate = useMarkGapDuplicate(tenantId, hotelId);
  const answer = useAnswerGap(tenantId, hotelId);

  const [activeGapId, setActiveGapId] = useState<string | null>(null);
  const [mode, setMode] = useState<'answer' | 'resolve' | null>(null);
  const [faqId, setFaqId] = useState('');
  const [question, setQuestion] = useState('');
  const [answerText, setAnswerText] = useState('');
  const [answerCategory, setAnswerCategory] = useState<KnowledgeCategory>('services');
  const [answerLanguage, setAnswerLanguage] = useState('en');

  if (gapsQuery.isLoading) {
    return <LoadingState data-testid="knowledge-gaps-loading" title={t('states.loading')} />;
  }
  if (gapsQuery.isError) {
    return (
      <ErrorState
        data-testid="knowledge-gaps-error"
        title={t('states.errorTitle')}
        description={t('states.errorDescription')}
        action={
          <Button
            variant="secondary"
            data-testid="knowledge-gaps-retry-btn"
            onClick={() => void gapsQuery.refetch()}
          >
            {t('states.retry')}
          </Button>
        }
      />
    );
  }

  const gaps = gapsQuery.data ?? [];
  if (gaps.length === 0) {
    return (
      <EmptyState
        data-testid="knowledge-gaps-empty"
        title={t('gaps.title')}
        description={t('gaps.empty')}
      />
    );
  }

  function openAnswer(gap: KnowledgeGap) {
    setActiveGapId(gap.id);
    setMode('answer');
    setQuestion(asText(gap.questionPreview) ?? '');
    setAnswerText('');
    setAnswerLanguage(asText(gap.language) ?? 'en');
  }

  function openResolve(gapId: string) {
    setActiveGapId(gapId);
    setMode('resolve');
    setFaqId('');
  }

  function closeForm() {
    setActiveGapId(null);
    setMode(null);
  }

  function submitAnswer(gapId: string) {
    answer.mutate(
      {
        gapId,
        body: {
          question,
          approvedAnswer: answerText,
          category: answerCategory,
          language: answerLanguage,
        },
      },
      {
        onSuccess: (faq) => {
          onFaqCreated(faq);
          closeForm();
        },
      },
    );
  }

  function submitResolve(gapId: string) {
    resolve.mutate({ gapId, body: { faqId } }, { onSuccess: closeForm });
  }

  return (
    <div className="flex flex-col gap-4">
      <Table data-testid="knowledge-gap-table">
        <TableHeader>
          <TableRow>
            <TableHead>{t('gaps.columnQuestion')}</TableHead>
            <TableHead>{t('gaps.columnLanguage')}</TableHead>
            <TableHead>{t('gaps.columnScore')}</TableHead>
            <TableHead>{t('gaps.columnStatus')}</TableHead>
            <TableHead>{t('gaps.columnDetected')}</TableHead>
            <TableHead aria-hidden />
          </TableRow>
        </TableHeader>
        <TableBody>
          {gaps.map((gap) => {
            const preview = asText(gap.questionPreview);
            const score = asNumber(gap.retrievalScore);
            const open = gap.status === 'needs_answer';
            return (
              <TableRow key={gap.id} data-testid={`knowledge-gap-row-${gap.id}`}>
                <TableCell>
                  <span className="text-[var(--color-charcoal)]">
                    {preview ?? (
                      <span className="text-[var(--color-stone-mid)]">{t('gaps.noPreview')}</span>
                    )}
                  </span>
                </TableCell>
                <TableCell>{asText(gap.language) ?? '—'}</TableCell>
                <TableCell className="text-sm text-[var(--color-stone-mid)]">
                  {score !== null ? score.toFixed(2) : '—'}
                </TableCell>
                <TableCell>
                  <StatusBadge
                    data-testid={`knowledge-gap-status-${gap.id}`}
                    status={gapStatusVariant(gap.status)}
                    label={t(`status.${gap.status}`)}
                  />
                </TableCell>
                <TableCell className="text-sm text-[var(--color-stone-mid)]">
                  {gap.createdAt}
                </TableCell>
                <TableCell>
                  {open && (
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button
                        size="sm"
                        variant="primary"
                        data-testid={`knowledge-gap-answer-btn-${gap.id}`}
                        onClick={() => openAnswer(gap)}
                      >
                        {t('gaps.answer')}
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        data-testid={`knowledge-gap-resolve-btn-${gap.id}`}
                        onClick={() => openResolve(gap.id)}
                      >
                        {t('gaps.resolve')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`knowledge-gap-dismiss-btn-${gap.id}`}
                        onClick={() => dismiss.mutate({ gapId: gap.id })}
                      >
                        {t('gaps.dismiss')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`knowledge-gap-duplicate-btn-${gap.id}`}
                        onClick={() => duplicate.mutate({ gapId: gap.id })}
                      >
                        {t('gaps.duplicate')}
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {activeGapId && mode === 'answer' && (
        <form
          data-testid="knowledge-gap-answer-form"
          className="flex flex-col gap-3 rounded-lg border border-[var(--color-warm-stone-soft)] p-4"
          onSubmit={(e) => {
            e.preventDefault();
            submitAnswer(activeGapId);
          }}
        >
          <h3 className="font-medium text-[var(--color-charcoal)]">{t('gaps.answerTitle')}</h3>
          <div className="flex flex-col gap-1">
            <Label htmlFor="gap-answer-question">{t('gaps.answerQuestionLabel')}</Label>
            <Input
              id="gap-answer-question"
              data-testid="knowledge-gap-answer-question-input"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="gap-answer-text">{t('gaps.answerLabel')}</Label>
            <Textarea
              id="gap-answer-text"
              data-testid="knowledge-gap-answer-text-input"
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor="gap-answer-category">{t('gaps.answerCategoryLabel')}</Label>
              <select
                id="gap-answer-category"
                data-testid="knowledge-gap-answer-category-select"
                className="h-9 rounded-md border border-[var(--color-warm-stone-soft)] bg-[var(--color-canvas)] px-3 text-sm"
                value={answerCategory}
                onChange={(e) => setAnswerCategory(e.target.value as KnowledgeCategory)}
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {t(`category.${c}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="gap-answer-language">{t('gaps.answerLanguageLabel')}</Label>
              <Input
                id="gap-answer-language"
                data-testid="knowledge-gap-answer-language-input"
                value={answerLanguage}
                onChange={(e) => setAnswerLanguage(e.target.value)}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              type="submit"
              size="sm"
              data-testid="knowledge-gap-answer-submit-btn"
              disabled={answer.isPending}
            >
              {answer.isPending ? t('actions.saving') : t('gaps.answerSubmit')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              data-testid="knowledge-gap-answer-cancel-btn"
              onClick={closeForm}
            >
              {t('gaps.cancel')}
            </Button>
          </div>
        </form>
      )}

      {activeGapId && mode === 'resolve' && (
        <form
          data-testid="knowledge-gap-resolve-form"
          className="flex flex-col gap-3 rounded-lg border border-[var(--color-warm-stone-soft)] p-4"
          onSubmit={(e) => {
            e.preventDefault();
            submitResolve(activeGapId);
          }}
        >
          <div className="flex flex-col gap-1">
            <Label htmlFor="gap-resolve-faq">{t('gaps.resolveFaqIdLabel')}</Label>
            <Input
              id="gap-resolve-faq"
              data-testid="knowledge-gap-resolve-faq-input"
              placeholder={t('gaps.resolveFaqIdPlaceholder')}
              value={faqId}
              onChange={(e) => setFaqId(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button
              type="submit"
              size="sm"
              data-testid="knowledge-gap-resolve-submit-btn"
              disabled={resolve.isPending || faqId.length === 0}
            >
              {resolve.isPending ? t('actions.saving') : t('gaps.resolve')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              data-testid="knowledge-gap-resolve-cancel-btn"
              onClick={closeForm}
            >
              {t('gaps.cancel')}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
